import {
  ShieldCheck,
  UtensilsCrossed,
  ShoppingBag,
  BarChart3,
  Receipt,
  Package,
  Wrench,
  CalendarDays,
  UserRound,
  CreditCard,
  FileText,
  Factory,
  TrendingUp,
  Boxes,
  HandCoins,
  UsersRound,
} from "lucide-react";
import type { ModuleStatusCardProps } from "../../types/dashboard";
import type { ModulesSettings } from "../../utils/zod/gym";
import type { ComponentType } from "react";

interface ModuleEntry {
  key: keyof ModulesSettings;
  label: string;
  icon: ComponentType<{ className?: string }>;
  managerOnly: boolean;
}

const MODULE_LIST: ModuleEntry[] = [
  {
    key: "planSales",
    label: "Venta de Planes",
    icon: CreditCard,
    managerOnly: false,
  },
  {
    key: "accessControl",
    label: "Control de Acceso",
    icon: ShieldCheck,
    managerOnly: false,
  },
  {
    key: "foodSales",
    label: "Ventas de Comida",
    icon: UtensilsCrossed,
    managerOnly: false,
  },
  {
    key: "merchandiseSales",
    label: "Mercadería",
    icon: ShoppingBag,
    managerOnly: false,
  },
  {
    key: "bookings",
    label: "Reservas",
    icon: CalendarDays,
    managerOnly: false,
  },
  {
    key: "trainers",
    label: "Entrenadores",
    icon: UserRound,
    managerOnly: false,
  },
  { key: "expenses", label: "Gastos", icon: Receipt, managerOnly: true },
  { key: "metrics", label: "Métricas", icon: BarChart3, managerOnly: true },
  { key: "inventory", label: "Inventario", icon: Package, managerOnly: true },
  { key: "machinery", label: "Maquinaria", icon: Wrench, managerOnly: true },
  {
    key: "productCatalog",
    label: "Catálogo Productos",
    icon: Boxes,
    managerOnly: true,
  },
  {
    key: "salesOrders",
    label: "Órdenes de Venta",
    icon: FileText,
    managerOnly: true,
  },
  { key: "invoicing", label: "Facturación", icon: Factory, managerOnly: true },
  {
    key: "productionMetrics",
    label: "Métricas B2B",
    icon: TrendingUp,
    managerOnly: true,
  },
  {
    key: "otherIncome",
    label: "Otros Ingresos",
    icon: HandCoins,
    managerOnly: true,
  },
  {
    key: "b2bCrm",
    label: "CRM B2B",
    icon: UsersRound,
    managerOnly: true,
  },
];

export const ModuleStatusCard = ({
  modulesSettings,
  role,
}: ModuleStatusCardProps) => {
  const isManager = role === "MANAGER";

  const visibleModules = MODULE_LIST.filter(
    (mod) => !mod.managerOnly || isManager,
  );

  return (
    <div className="card-compact flex flex-col gap-4 @container">
      <div>
        <h2 className="text-size-base font-semibold text-global-title">
          Módulos Activos
        </h2>
        <p className="text-size-xs text-global-muted mt-0.5">
          Funcionalidades habilitadas para este gimnasio
        </p>
      </div>

      <div
        className="grid grid-cols-1 @2xs:grid-cols-2 @sm:grid-cols-3 gap-2"
        role="list"
      >
        {visibleModules.map((mod) => {
          const enabled = modulesSettings?.[mod.key]?.enabled ?? false;
          return (
            <div
              key={mod.key}
              role="listitem"
              className="flex items-start gap-1.5 px-2 py-2 sm:px-3 rounded-lg border border-global-primary bg-global-overlay min-w-0"
            >
              <mod.icon
                className={`w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 ${enabled ? "text-global-accent" : "text-global-muted"}`}
                aria-hidden="true"
              />
              <span
                className={`text-size-2xs sm:text-size-xs font-medium leading-tight flex-1 min-w-0 ${enabled ? "text-global-title" : "text-global-muted"}`}
              >
                {mod.label}
              </span>
              <span
                className={`shrink-0 text-[9px] sm:text-[10px] font-semibold px-1 sm:px-1.5 py-0.5 rounded-full ${
                  enabled
                    ? "bg-global-overlay-secondary text-global-success border border-global-primary"
                    : "bg-global-overlay text-global-muted border border-global-primary opacity-50"
                }`}
                aria-label={enabled ? "activo" : "inactivo"}
              >
                {enabled ? "ON" : "OFF"}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
